import { useState, useEffect } from 'react';
import { Radio, Shield, AlertTriangle, Filter, Wifi, WifiOff } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

const IMPORTANCE_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#6b7280' };
const SENTIMENT_ICONS = { bullish: '▲', bearish: '▼', neutral: '—' };
const SENTIMENT_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280' };
const BIAS_COLORS = {
  neutral_data: '#3b82f6', neutral_institutional: '#6366f1', bearish_macro: '#ef4444',
  bullish_crypto: '#10b981', asia_crypto: '#f59e0b', geopolitics_regional: '#f97316',
  geopolitics_mixed: '#8b5cf6', geopolitics_eu: '#06b6d4', geopolitics_ru: '#dc2626',
  geopolitics_unknown: '#9ca3af',
};

function TrustBadge({ score }) {
  const color = score >= 50 ? '#10b981' : score >= 30 ? '#f59e0b' : '#ef4444';
  return <span className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{score}</span>;
}

function ImportanceBadge({ importance }) {
  const color = IMPORTANCE_COLORS[importance] || '#6b7280';
  return <span className="text-[8px] font-mono uppercase font-bold px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{importance}</span>;
}

export default function TelegramFeed() {
  const { setPage } = usePage();
  useEffect(() => { setPage('telegram'); }, []);

  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [critical, setCritical] = useState([]);
  const [filter, setFilter] = useState({ channel: '', importance: '', sentiment: '' });

  useEffect(() => {
    async function load() {
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      setStatus(await json('/api/telegram/status'));
      setChannels(await json('/api/telegram/channels') || []);
      const params = new URLSearchParams({ limit: '50' });
      if (filter.channel) params.set('channel', filter.channel);
      if (filter.importance) params.set('importance', filter.importance);
      if (filter.sentiment) params.set('sentiment', filter.sentiment);
      setMessages(await json(`/api/telegram/messages/recent?${params}`) || []);
      setCritical(await json('/api/telegram/messages/critical') || []);
    }
    load();
    const msgInterval = setInterval(load, 30000);
    return () => clearInterval(msgInterval);
  }, [filter.channel, filter.importance, filter.sentiment]);

  const activeChannels = channels.filter(c => c.enabled);
  const avgTrust = activeChannels.length > 0 ? Math.round(activeChannels.reduce((s, c) => s + c.trust_score, 0) / activeChannels.length) : 0;
  const todayCount = messages.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Radio className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Telegram Intelligence Feed</h1>
        <span className="text-korinth-muted text-sm font-mono">HERMES · Messenger</span>
        {status && (
          <span className={`flex items-center gap-1 text-xs font-mono ${status.connected ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
            {status.connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {status.connected ? 'Connected' : 'Not connected'}
          </span>
        )}
      </div>

      {!status?.connected && (
        <div className="bg-korinth-danger/10 border border-korinth-danger/30 rounded-lg px-4 py-3 text-sm font-mono text-korinth-danger">
          Telegram client not connected. Run <code className="bg-korinth-bg px-1 rounded">node scripts/telegram-auth.js</code> on the server to authenticate, then restart the backend.
        </div>
      )}

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Messages today:</span> <span className="text-white font-bold">{todayCount}</span></span>
        <span><span className="text-korinth-muted">Critical:</span> <span className="text-korinth-danger font-bold">{critical.length}</span></span>
        <span><span className="text-korinth-muted">Active channels:</span> <span className="text-korinth-accent font-bold">{(channels || []).filter(c => c.enabled).length}</span></span>
      </div>

      {/* Sprint 20: Portfolio impact */}
      <TelegramPortfolioImpact />

      {/* Critical alerts */}
      {critical.length > 0 && (
        <div className="space-y-2">
          {critical.slice(0, 3).map(m => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-2 rounded-lg bg-korinth-danger/10 border border-korinth-danger/30">
              <AlertTriangle className="w-4 h-4 text-korinth-danger flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-mono text-korinth-muted">{m.display_name} — {m.sent_at?.slice(11, 19)}</span>
                <p className="text-xs font-mono text-white truncate">{m.summary || m.message_text?.slice(0, 120)}</p>
              </div>
              <div className="flex gap-1">
                {(m.affected_assets || []).slice(0, 3).map(a => (
                  <span key={a} className="text-[8px] font-mono px-1 py-0.5 rounded bg-korinth-border text-korinth-text">{a}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Messages Today', value: todayCount },
          { label: 'Active Channels', value: `${activeChannels.length}/${channels.length}` },
          { label: 'Avg Trust', value: avgTrust },
          { label: 'Critical (24h)', value: critical.length },
        ].map(s => (
          <div key={s.label} className="bg-korinth-surface border border-korinth-border rounded-lg px-3 py-2 text-center">
            <div className="text-lg font-mono font-bold text-white">{s.value}</div>
            <div className="text-[9px] text-korinth-muted">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Channel Trust Panel */}
      <ModuleContainer title="Channel Trust Panel" subtitle={`${activeChannels.length} active channels, 14-day probation period`}>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          {channels.map(ch => (
            <div key={ch.handle} className={`bg-korinth-bg rounded px-2.5 py-2 border ${ch.enabled ? 'border-korinth-border/50' : 'border-korinth-danger/30 opacity-50'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-mono font-bold text-white truncate">{ch.display_name}</span>
                <TrustBadge score={ch.trust_score} />
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-[8px] font-mono px-1 py-0.5 rounded" style={{ color: BIAS_COLORS[ch.bias] || '#9ca3af', backgroundColor: (BIAS_COLORS[ch.bias] || '#9ca3af') + '15' }}>{ch.bias?.replace(/_/g, ' ')}</span>
                {ch.onProbation && <span className="text-[7px] font-mono px-1 py-0.5 rounded bg-yellow-500/10 text-yellow-400">PROBATION</span>}
              </div>
              <div className="flex justify-between mt-1 text-[8px] text-korinth-muted font-mono">
                <span>{ch.total_messages || 0} msgs</span>
                <span>{ch.corroborated_count || 0} corr.</span>
              </div>
            </div>
          ))}
        </div>
      </ModuleContainer>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-korinth-muted" />
        <select value={filter.channel} onChange={e => setFilter(f => ({ ...f, channel: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-korinth-text">
          <option value="">All channels</option>
          {channels.map(c => <option key={c.handle} value={c.handle}>{c.display_name}</option>)}
        </select>
        <select value={filter.importance} onChange={e => setFilter(f => ({ ...f, importance: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-korinth-text">
          <option value="">All importance</option>
          {['critical', 'high', 'medium', 'low'].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={filter.sentiment} onChange={e => setFilter(f => ({ ...f, sentiment: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-xs font-mono text-korinth-text">
          <option value="">All sentiment</option>
          {['bullish', 'bearish', 'neutral'].map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {/* Message Feed */}
      <ModuleContainer title="Live Message Feed" subtitle={`${messages.length} messages`}>
        <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
          {messages.length > 0 ? messages.map(m => (
            <div key={m.id} className="flex items-start gap-2 px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30 hover:border-korinth-border/60 transition-colors">
              <div className="flex-shrink-0 w-16 text-[8px] font-mono text-korinth-muted pt-0.5">
                {m.sent_at?.slice(11, 19)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <span className="text-[9px] font-mono font-bold text-korinth-text">{m.display_name}</span>
                  <ImportanceBadge importance={m.importance} />
                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-korinth-border text-korinth-muted">{m.classification}</span>
                  <span className="text-[9px] font-mono" style={{ color: SENTIMENT_COLORS[m.sentiment] }}>
                    {SENTIMENT_ICONS[m.sentiment]}
                  </span>
                  {m.corroborated_by && <span className="text-[7px] font-mono px-1 py-0.5 rounded bg-green-500/10 text-green-400">CORR</span>}
                </div>
                <p className="text-[10px] font-mono text-korinth-text leading-relaxed">{m.summary || m.message_text?.slice(0, 200)}</p>
                {(m.affected_assets || []).length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {m.affected_assets.map(a => (
                      <span key={a} className="text-[7px] font-mono px-1 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent">{a}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 text-right">
                <div className="text-[10px] font-mono font-bold" style={{ color: IMPORTANCE_COLORS[m.importance] || '#6b7280' }}>
                  {m.adjusted_impact_score || 0}
                </div>
                <div className="text-[7px] text-korinth-muted">score</div>
              </div>
            </div>
          )) : (
            <p className="text-[10px] text-korinth-muted font-mono text-center py-8">
              {status?.connected ? 'No classified messages yet — waiting for data...' : 'Connect Telegram to see messages'}
            </p>
          )}
        </div>
      </ModuleContainer>

      {/* Sprint 20: Track record */}
      <TelegramTrackRecord />
    </div>
  );
}

function TelegramPortfolioImpact() {
  const { impact, loading } = usePageTracking('telegram');
  return <PortfolioImpactPanel topic="Telegram" {...impact} loading={loading} />;
}

function TelegramTrackRecord() {
  const { track, loading } = usePageTracking('telegram');
  return <TrackRecordPanel {...track} loading={loading} metric_name="Telegram Feed" />;
}
